import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ImageElement, VideoElement } from '@bynder-studio/render-core';
import { Box, Form, List, Select, Thumbnail } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconAdjust, IconArrowDown, IconArrowUp, IconVolumeOff, IconVolumeUp } from '@bynder/icons';
import useVariations from 'packages/pages/design/hooks/useVariations';
import BrowseButton from 'packages/pages/editor/RightSideMenu/FormComponents/BrowseButton';
import Scale from 'packages/pages/design/components/Scale';
import HorizontalOffset from 'packages/pages/design/components/HorizontalOffset';
import VerticalOffset from 'packages/pages/design/components/VerticalOffset';
import HorizontalAlignment from 'packages/pages/design/components/HorizontalAlignment';
import VerticalAlignment from 'packages/pages/design/components/VerticalAlignment';
import useElement from 'packages/pages/design/hooks/useElement';
import useDesign from 'packages/pages/design/hooks/useDesign';
import useAudioHook from 'packages/hooks/useElementAudio';
import useAccessRights from 'packages/hooks/useAccessRights';
import { flattenTree } from 'packages/common/editor/helpers/elementtree';
import {
    getElementPropertiesFromSource,
    getFreeCropModeParams,
    getResetContentTransformOnSourceChange,
    syncLinkedContentPropertyTransforms,
} from 'packages/common/editor/helpers/index';
import { ContentType } from 'packages/pages/editor/RightSideMenu/FormComponents/BrowseButton/types';
import { AudioFeature } from 'packages/pages/editor/RightSideMenu/FormComponents/AudioFeature';
import { ActiveSplits } from 'packages/store/user/types';

const cropModeOptions = [
    { value: 'CONTAIN', label: 'Contain' },
    { value: 'COVER', label: 'Cover' },
    { value: 'COVER_HEIGHT', label: 'Height' },
    { value: 'COVER_WIDTH', label: 'Width' },
    { value: 'FREE', label: 'Free' },
];

type Props = {
    id: number;
    disabled: boolean;
    type: 'image' | 'video';
    isLoading: boolean;
    isDisabledByDeletedAsset?: boolean;
};

const MediaElement = ({ id, disabled, type, isLoading, isDisabledByDeletedAsset }: Props) => {
    const { translate } = useTranslate();
    const { frameRate, creativeType, recalculateShots, creativeModel, template } = useDesign();
    const { key, element, updateElement, syncContentProperty } = useElement<VideoElement | ImageElement>(id);
    const { updateVariationElement, removeDeletedAsset } = useVariations();
    const [cropMode, setCropMode] = useState(element?.contentTransform?.type || 'CONTAIN');
    const [isExpanded, setIsExpanded] = useState(false);
    const { isSplitEnabled } = useAccessRights();
    const lockFeatureEnabled = isSplitEnabled(ActiveSplits.studioImageCropModeLock);
    const cropModeLocked = lockFeatureEnabled ? (element?.contentTransform?.locked ?? false) : false;
    const currentPageIndex = creativeModel.getCurrentPageIndex();

    const updateFn = useCallback(
        (params) => {
            if (element?.contentPropertyId) {
                syncContentProperty(params);
            } else {
                updateElement(params);
            }
        },
        [element?.contentPropertyId, syncContentProperty, updateElement],
    );

    const { useAudio, gain, fadeIn, fadeOut, setUseAudio, setGain, setFadeInOut } = useAudioHook({
        audio: element as VideoElement,
        onAudioChange: updateFn,
    });

    const handleChangeCropMode = useCallback(
        (newValue) => {
            if (!element) {
                return;
            }

            setCropMode(newValue);

            const { scale, ...baseTransform } = element.contentTransform;
            const minScale = Math.max(
                element.dimension.width / element.naturalDimension.width,
                element.dimension.height / element.naturalDimension.height,
            );
            const contentTransform = {
                ...baseTransform,
                type: newValue,
                ...(newValue === 'FREE' ? getFreeCropModeParams(element) : {}),
                ...(newValue === 'COVER' ? { scale: minScale } : {}),
            };

            updateElement({ contentTransform });
        },
        [element?.contentTransform, element, updateElement],
    );

    const isDynamicLengthAvailable = element instanceof VideoElement ? element.useDynamicLength : false;

    const handleSelectSource = useCallback(
        (source) => {
            const params = getElementPropertiesFromSource(element, creativeType, source);
            const { duration } = params;
            delete params.duration;

            const resetDefault = element.srcId !== source.id;

            if (resetDefault) {
                const resetContentTransform = getResetContentTransformOnSourceChange(element, params.naturalDimension);

                if (resetContentTransform) {
                    params.contentTransform = resetContentTransform;
                }
            }

            updateFn(params);

            const modelsMetaData = creativeModel.getModelsMetaData();

            syncLinkedContentPropertyTransforms({
                creativeModel,
                sourceElement: element,
                contentTransform: params.contentTransform,
                naturalDimension: params.naturalDimension,
                onLinkedElementUpdated: (modelIndex, linkedElementId, linkedParams) => {
                    updateVariationElement(modelsMetaData[modelIndex]?.id, linkedElementId, linkedParams);
                },
            });

            if (duration && isDynamicLengthAvailable) {
                recalculateShots([{ elementId: element.id, duration }]).then((elements) => {
                    if (!elements.length) {
                        return;
                    }

                    const models = creativeModel.getModels();
                    const modelsMetaData = creativeModel.getModelsMetaData();

                    models.forEach((model, i) => {
                        model.updateElements(elements);
                        const sizeId = modelsMetaData[i]?.id;
                        elements.forEach((item) => {
                            updateVariationElement(sizeId, item.id, item);
                        });
                    });
                });
            }

            removeDeletedAsset(id);
        },
        [
            updateFn,
            element,
            updateElement,
            syncContentProperty,
            creativeType,
            creativeModel,
            updateVariationElement,
            removeDeletedAsset,
        ],
    );

    const collectionId = useMemo(() => {
        const desiredElement: any = Object.values(flattenTree(template.pages[currentPageIndex].elements)).find(
            (el: any) => el.id === id,
        );

        if (!desiredElement) {
            return null;
        }

        return desiredElement.properties.value?.bynderCollectionId;
    }, [template.pages, currentPageIndex, id]);

    const isVideoElement = type === 'video';

    useEffect(() => {
        setCropMode(element?.contentTransform?.type || 'CONTAIN');
    }, [key, element?.contentTransform]);

    return (
        <>
            {!isVideoElement && (
                <Form.Group>
                    <BrowseButton
                        type={type as ContentType}
                        frameRate={frameRate}
                        selectedObject={{ element }}
                        disabled={disabled || isLoading}
                        loading={isLoading}
                        mediaSrc={element?.src || ''}
                        collectionId={collectionId}
                        isAlpha={false}
                        isEditorPage={false}
                        testId="design_scene_image_asset"
                        handleSelectSource={handleSelectSource}
                        resizableRange={isDynamicLengthAvailable}
                    />
                </Form.Group>
            )}
            {isVideoElement && (
                <Form.Group>
                    <BrowseButton
                        type={type as ContentType}
                        frameRate={frameRate}
                        selectedObject={{ element }}
                        handleSelectSource={handleSelectSource}
                        disabled={disabled || isLoading}
                        loading={isLoading}
                        resizableRange={isDynamicLengthAvailable}
                        mediaSrc={element?.src || ''}
                        isAlpha={element instanceof VideoElement ? element.isAlpha : undefined}
                        collectionId={collectionId}
                        isEditorPage={false}
                        testId={`design_scene_${type.toLowerCase()}_asset`}
                    />
                </Form.Group>
            )}
            <List.Item
                thumbnail={<Thumbnail icon={<IconAdjust />} />}
                rightElements={isExpanded ? <IconArrowUp /> : <IconArrowDown />}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                {translate('design.sidebar.scene.options.expand.label', {
                    type: type.charAt(0).toUpperCase() + type.slice(1).toLowerCase(),
                })}
            </List.Item>
            {isExpanded && (
                <Box paddingBlock="3">
                    {!cropModeLocked && (
                        <Form.Group>
                            <Form.Label id="scene_crop_mode">
                                {translate('design.sidebar.scene.options.crop-mode.label')}
                            </Form.Label>
                            <Select
                                value={cropMode}
                                onChange={handleChangeCropMode}
                                label={cropModeOptions.find((i) => i.value === cropMode)?.label}
                                aria-labelledby="scene_crop_mode"
                                isDisabled={disabled}
                            >
                                {cropModeOptions.map((item) => (
                                    <Select.Item value={item.value} key={item.value}>
                                        {item.label}
                                    </Select.Item>
                                ))}
                            </Select>
                        </Form.Group>
                    )}
                    {cropMode === 'FREE' ? (
                        <>
                            <Scale id={id} disabled={disabled || isDisabledByDeletedAsset} />
                            <HorizontalOffset id={id} disabled={disabled || isDisabledByDeletedAsset} />
                            <VerticalOffset id={id} disabled={disabled || isDisabledByDeletedAsset} />
                        </>
                    ) : (
                        <>
                            {cropMode === 'COVER' && <Scale id={id} disabled={disabled || isDisabledByDeletedAsset} />}
                            {cropMode !== 'COVER_WIDTH' && (
                                <HorizontalAlignment id={id} disabled={disabled || isDisabledByDeletedAsset} />
                            )}
                            {cropMode !== 'COVER_HEIGHT' && (
                                <VerticalAlignment id={id} disabled={disabled || isDisabledByDeletedAsset} />
                            )}
                        </>
                    )}
                    {isVideoElement && (
                        <>
                            <List.Item
                                thumbnail={
                                    <Thumbnail size="m" icon={useAudio ? <IconVolumeUp /> : <IconVolumeOff />} />
                                }
                                checkedVariant="switch"
                                onClick={() => {
                                    setUseAudio(!useAudio);
                                }}
                                isChecked={useAudio}
                                isDisabled={disabled}
                            >
                                {translate('design.sidebar.scene.options.crop-mode.use-audio.label')}
                            </List.Item>
                            {useAudio && (
                                <AudioFeature
                                    gain={gain}
                                    fadeIn={fadeIn}
                                    fadeOut={fadeOut}
                                    onFadeInChange={setFadeInOut('fadeIn')}
                                    onFadeOutChange={setFadeInOut('fadeOut')}
                                    onGainChange={setGain}
                                    isDisabled={disabled}
                                />
                            )}
                        </>
                    )}
                </Box>
            )}
        </>
    );
};

export default memo(MediaElement);
